import { useRef, useState } from 'react';
import { Paperclip } from '@phosphor-icons/react';

// Every "file attachment" box in the app (NewTicketPage, DispatcherPage,
// TicketProcessPage) was styled as a dashed dropzone and its own label
// literally said "فایل را اینجا بکشید یا کلیک کنید" (drag the file here or
// click) but never had onDragOver/onDrop handlers wired up - only the click
// path (hidden <input type="file">) ever actually worked. This component is
// the real, reusable dropzone: `onFile(file)` fires from either a drag-drop
// or a click-to-pick, so callers only need one handler that takes a raw
// File (not an input change event).
export default function FileDropZone({
  onFile,
  hint = 'فایل را اینجا بکشید یا کلیک کنید',
  accept,
  limitsHint,
  compact = false,
  style: extra,
}) {
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const dragDepth = useRef(0);

  const pick = (f) => { if (f) onFile(f); };

  return (
    <div
      onClick={() => fileRef.current?.click()}
      onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragOver(true); }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
      onDragLeave={(e) => {
        e.preventDefault();
        // A dragleave fires when the pointer crosses onto a child element
        // too, not just when it truly exits the box - a depth counter
        // stops the highlight from flickering off while dragging over the
        // icon/text inside.
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragOver(false);
        pick(e.dataTransfer.files?.[0]);
      }}
      style={{
        border: `2px dashed ${dragOver ? '#29AAE1' : '#B7C9EC'}`,
        borderRadius: 10, padding: compact ? 16 : 20, textAlign: 'center',
        cursor: 'pointer', background: dragOver ? '#EDF3FC' : '#f8faff',
        transition: 'border-color 0.15s, background 0.15s',
        ...extra,
      }}
    >
      <div style={{ marginBottom: compact ? 4 : 6, display: 'flex', justifyContent: 'center' }}>
        <Paperclip size={compact ? 22 : 28} weight="regular" />
      </div>
      <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{hint}</p>
      {limitsHint && (
        <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>{limitsHint}</p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }}
      />
    </div>
  );
}
